import {
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import dayjs from "configs/dayjs.config";

import { EXECUTION_TIMELINE_FIELDS } from "../constants/product.constants";
import TaskPreviewModal from "components/Task/TaskPreviewModal";
import { TaskMeta } from "components/Task/TaskMeta";
import { useGlobalContext } from "context/GlobalContext";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProductExecutionTimelineApi } from "API/apis/product/getProductExecutionTimeline";
import { message } from "constants/responses/message";
import { approveExecutionTimelineApi } from "API/apis/product/approveExecutionTimeline";
import { dateTimeFormat } from "constants/componentsConstants";
import { FloatingStatusButton } from "components/FloatingButton/FloatingStatusButton";
import { executionTimelineTaskStatusApi } from "API/apis/product/executionTimelineTaskStatus";
import { getTaskFromLotIdApi } from "API/apis/product/getTaskFromLotId";
import { Box } from "@mui/system";
import { getExecutionTimelineAssigneeByLotIdApi } from "API/apis/product/getExecutionTimelineAssigneeByLotId";

const convertAssigneeToKeyValue = (data) => {
  let segregatedData;
  data.forEach((item) => {
    segregatedData = {
      ...segregatedData,
      [item.field]: item.name,
    };
  });
  return segregatedData;
};

export const ExecutionTimelinePreview = () => {
  const { id } = useParams();
  const { setIsInitializing, setError, setIsLoading, pageAccess, setSuccess } =
    useGlobalContext();
  const [reloadRequired, setReloadRequired] = useState(true);
  const [taskData, setTaskData] = useState({});
  const [timelineAssignee, setTimelineAssignee] = useState({});
  const [taskStatus, setTaskStatus] = useState({});
  const [timelineData, setTimelineData] = useState({});
  const [isApproved, setIsApproved] = useState(false);
  let startDate;
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitializing(true);
        const promises = [
          getProductExecutionTimelineApi(id),
          executionTimelineTaskStatusApi(id),
          getTaskFromLotIdApi(id),
          getExecutionTimelineAssigneeByLotIdApi(id),
        ];
        const [
          executionTimeline,
          executionTimelineTaskStatus,
          executionTimelineTasks,
          executionTimelineAssignee,
        ] = await Promise.all(promises);
        if (
          executionTimeline.status !== 200 ||
          executionTimelineTasks.status !== 200 ||
          executionTimelineTaskStatus.status !== 200 ||
          executionTimelineAssignee.status !== 200
        ) {
          throw new Error(message.error.generalFailure);
        }
        setIsApproved(executionTimeline.data[0].isApproved);
        setTimelineData(executionTimeline.data[0]);
        setTaskStatus(executionTimelineTaskStatus.data);
        setTaskData(executionTimelineTasks.data);
        setTimelineAssignee(
          convertAssigneeToKeyValue(executionTimelineAssignee.data)
        );
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, reloadRequired]);

  const handleChangeApprovalStatus = async () => {
    try {
      const { data: dataResponse, status } = await approveExecutionTimelineApi({
        productLotProcessingId: id,
        status: !isApproved,
      });
      if (status === 204) {
        throw new Error(message.info.noRecordsModified);
      }
      setIsApproved(!isApproved);
      setSuccess(dataResponse.message);
      setReloadRequired(!reloadRequired);
    } catch (error) {
      setError(error.message);
    }
  };
  return (
    <>
      <Box justifyContent="space-between">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Product Execution Timeline
        </Typography>
        <FlexBox>
          {pageAccess.canApproval && (
            <Button
              variant="contained"
              onClick={handleChangeApprovalStatus}
              color={!isApproved ? "success" : "error"}
            >
              {!isApproved ? "Approve" : "Un-Approve"}
            </Button>
          )}
          <Divider variant="middle" />
          <Typography component="span">
            {isApproved
              ? "Approved By : "
              : timelineData.approvedOn
              ? "UnApproved By : "
              : ""}
            {timelineData.approvedBy}
          </Typography>
        </FlexBox>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timeline</TableCell>
              <TableCell>Day(s)</TableCell>
              <TableCell>Deadline</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Task Status</TableCell>
              <TableCell>Task Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key="startDate">
              <TableCell>Start Date</TableCell>
              <TableCell></TableCell>
              <TableCell>
                {dayjs(startDate).format(dateTimeFormat.date)}
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
            {Object.entries(EXECUTION_TIMELINE_FIELDS).map(
              ([key, value], index) => {
                if (startDate === "") {
                  startDate = dayjs(timelineData[key]);
                } else {
                  startDate = dayjs(startDate).add(timelineData[key], "day");
                }
                return (
                  <TableRow key={index}>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {EXECUTION_TIMELINE_FIELDS[key]}
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {timelineData[key]} Day(s)
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {dayjs(startDate).format(dateTimeFormat.date)}
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {timelineAssignee?.[key] || ""}
                    </TableCell>
                    <TableCell>
                      <TaskPreviewModal
                        task={{
                          title: taskData?.[value]?.docCode,
                          docDetails: taskData?.[value]?.docDetails,
                          detail: taskData?.[value]?.taskDetails,
                          description: taskData?.[value]?.taskDescription,
                          taskByName: taskData?.[value]?.taskBy.label,
                          taskToName: taskData?.[value]?.taskTo.label,
                          taskToOn: taskData?.[value]?.taskToOn,
                          acceptanceStatus: taskData?.[value]?.acceptanceStatus,
                          acceptanceComment:
                            taskData?.[value]?.acceptanceComment,
                          acceptanceOn: taskData?.[value]?.acceptanceOn,
                          deadline: taskData?.[value]?.deadline,
                          doneStatus: taskData?.[value]?.doneStatus,
                          doneOn: taskData?.[value]?.doneOn,
                          acknowledgeStatus:
                            taskData?.[value]?.acknowledgeStatus,
                          acknowledgeOn: taskData?.[value]?.acknowledgeOn,
                        }}
                      >
                        <FlexBox>
                          {taskStatus[EXECUTION_TIMELINE_FIELDS[key]]
                            ?.created ? (
                            <>
                              <FloatingStatusButton label="C" />
                            </>
                          ) : (
                            ""
                          )}
                          {taskStatus[EXECUTION_TIMELINE_FIELDS[key]]
                            ?.acceptance ? (
                            <FloatingStatusButton label="A" />
                          ) : (
                            ""
                          )}
                          {taskStatus[EXECUTION_TIMELINE_FIELDS[key]]?.done ? (
                            <FloatingStatusButton label="D" />
                          ) : (
                            ""
                          )}
                          {taskStatus[EXECUTION_TIMELINE_FIELDS[key]]
                            ?.acknowledged ? (
                            <FloatingStatusButton label="K" />
                          ) : (
                            ""
                          )}
                        </FlexBox>
                      </TaskPreviewModal>
                    </TableCell>
                    <TableCell>
                      <TaskMeta
                        taskDetails={taskData?.[value]?.docDetails}
                        taskToName={taskData?.[value]?.taskTo.label}
                        taskByName={taskData?.[value]?.taskBy.label}
                        taskToOn={taskData?.[value]?.taskToOn}
                        acceptanceStatus={taskData?.[value]?.acceptanceStatus}
                        acceptanceOn={taskData?.[value]?.acceptanceOn}
                        deadline={taskData?.[value]?.deadline}
                        doneStatus={taskData?.[value]?.doneStatus}
                        doneOn={taskData?.[value]?.doneOn}
                        acknowledgeStatus={taskData?.[value]?.acknowledgeStatus}
                        acknowledgeOn={taskData?.[value]?.acknowledgeOn}
                      />
                    </TableCell>
                  </TableRow>
                );
              }
            )}
            <TableRow key="endDate">
              <TableCell>End Date</TableCell>
              <TableCell></TableCell>
              <TableCell>
                {dayjs(timelineData.startDate).format(dateTimeFormat.date)}
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
